import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { broker } from "@/config/broker.config";
import { siteConfig } from "@/config/website.config";
import { FiPhone, FiMail, FiCheck, FiArrowRight } from "@/lib/icons";

export function AboutPage() {
  return (
    <>
      {/* Hero Section */}
      <section className="bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div>
              <p className="section-label mb-4">About Us</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
                Meet your
                <br />
                <span className="italic">mortgage expert</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg">
                {broker.shortBio}
              </p>

              {/* Accreditation */}
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <img
                  src="/logo/mfa-amember-medallion.png"
                  alt="MFAA Member"
                  className="h-14 w-auto"
                />
                <img
                  src="/logo/afg.png"
                  alt="AFG - Australian Finance Group"
                  className="h-8 w-auto"
                  style={{ filter: "brightness(0) saturate(100%) invert(11%) sepia(50%) saturate(2000%) hue-rotate(180deg) brightness(95%) contrast(100%)" }}
                />
              </div>
              <div className="mt-4 space-y-1 text-sm text-muted-foreground">
                <p>ABN {siteConfig.business.abn}</p>
                <p>Credit Representative 373517 is authorised under Australian Credit Licence {siteConfig.business.acl}.</p>
              </div>

              {/* Quick Contact */}
              <div className="mt-6 flex flex-wrap gap-4">
                <Button asChild className="btn-pill h-11 px-6">
                  <a href={`tel:${broker.phone.replace(/\s/g, "")}`}>
                    <FiPhone className="mr-2 size-4" />
                    Call {broker.phone}
                  </a>
                </Button>
                <Button asChild variant="outline" className="btn-pill h-11 px-6">
                  <Link to="/contact">
                    Book Consultation
                    <FiArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Photo */}
            <div className="relative">
              <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-muted">
                <img
                  src={broker.image}
                  alt={broker.name}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Name Card Overlay */}
              <div className="absolute -bottom-6 left-6 right-6 bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-serif text-2xl mb-1">{broker.name}</h2>
                    <p className="text-muted-foreground">{broker.role}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <img
                      src="/logo/mfa-amember-medallion.png"
                      alt="MFAA Member"
                      className="h-12 w-auto"
                    />
                    <img
                      src="/logo/afg.png"
                      alt="AFG"
                      className="h-6 w-auto"
                      style={{ filter: "brightness(0) saturate(100%) invert(11%) sepia(50%) saturate(2000%) hue-rotate(180deg) brightness(95%) contrast(100%)" }}
                    />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <a
                    href={`mailto:${broker.email}`}
                    className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors"
                    aria-label="Email"
                  >
                    <FiMail className="size-4" />
                  </a>
                  <a
                    href={`tel:${broker.phone.replace(/\s/g, "")}`}
                    className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors"
                    aria-label="Phone"
                  >
                    <FiPhone className="size-4" />
                  </a>
                  {/* LinkedIn badge - commented out for now
                  {broker.socials?.linkedin && (
                    <a
                      href={broker.socials.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors"
                      aria-label="LinkedIn"
                    >
                      <FiLinkedin className="size-4" />
                    </a>
                  )}
                  */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="prose prose-lg max-w-none">
            {broker.bio.split("\n\n").map((paragraph, index) => (
              <p key={index} className="text-muted-foreground leading-relaxed mb-6">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Qualifications & Specialties */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Qualifications */}
            {broker.qualifications && (
              <div>
                <p className="section-label mb-4">Qualifications</p>
                <h2 className="font-serif text-3xl mb-8">
                  Professional accreditations
                </h2>
                <ul className="space-y-4">
                  {broker.qualifications.map((qual) => (
                    <li key={qual} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                        <FiCheck className="size-3.5 text-accent-foreground" />
                      </div>
                      <span className="text-foreground">{qual}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Specialties */}
            {broker.specialties && (
              <div>
                <p className="section-label mb-4">Specialties</p>
                <h2 className="font-serif text-3xl mb-8">Areas of expertise</h2>
                <ul className="space-y-4">
                  {broker.specialties.map((specialty) => (
                    <li key={specialty} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center shrink-0 mt-0.5">
                        <FiCheck className="size-3.5 text-accent-foreground" />
                      </div>
                      <span className="text-foreground">{specialty}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Why Choose Us</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl max-w-2xl mx-auto">
              A better way to get a home loan
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "70+ Lenders",
                description:
                  "Access to over 70 lenders including all major banks, giving you more options than going direct.",
              },
              {
                title: "Free Service",
                description:
                  "Our service costs you nothing. Lenders pay us a commission, so you get expert help at no extra cost.",
              },
              {
                title: "Personal Guidance",
                description:
                  "Work directly with an experienced broker who understands your situation and goals.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-muted rounded-2xl p-8 text-center"
              >
                <h3 className="font-serif text-2xl mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-4 pb-8">
        <div className="bg-primary rounded-[2.5rem] py-16 md:py-24 px-6 md:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="section-label mb-4">Let's Talk</p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-primary-foreground mb-4">
              Ready to find your perfect home loan?
            </h2>
            <p className="text-lg text-primary-foreground/90 mb-8 max-w-xl mx-auto">
              Book a free consultation with {broker.name.split(" ")[0]} to
              discuss your goals and explore your options.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90 px-8 h-12"
              >
                <Link to="/contact">
                  Book Free Consultation
                  <FiArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="btn-pill bg-white/10 border border-white/20 text-white hover:bg-white/20 px-8 h-12"
              >
                <a href={`tel:${broker.phone.replace(/\s/g, "")}`}>
                  <FiPhone className="mr-2 size-4" />
                  {broker.phone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}
